'use client';

import { Circle } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useContext,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
} from 'react';

import { cn } from '@/lib/utils';

type RadioGroupContextValue = {
  disabled?: boolean;
  name?: string;
  onValueChange?: (value: string) => void;
  required?: boolean;
  value?: string;
};

type RadioGroupProps = Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'onChange'> & {
  defaultValue?: string;
  disabled?: boolean;
  name?: string;
  onValueChange?: (value: string) => void;
  required?: boolean;
  value?: string;
};

type RadioGroupItemProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'value'> & {
  value: string;
};

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    { className, children, defaultValue, disabled, name, onValueChange, required, value: controlledValue, ...props },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const value = controlledValue ?? uncontrolledValue;

    const contextValue = useMemo<RadioGroupContextValue>(
      () => ({
        disabled,
        name,
        required,
        value,
        onValueChange: (nextValue) => {
          if (disabled) {
            return;
          }

          if (controlledValue == null) {
            setUncontrolledValue(nextValue);
          }

          onValueChange?.(nextValue);
        },
      }),
      [controlledValue, disabled, name, onValueChange, required, value]
    );

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <div
          ref={ref}
          role="radiogroup"
          aria-disabled={disabled || undefined}
          aria-required={required || undefined}
          className={cn('grid gap-2', className)}
          {...props}
        >
          {children}
        </div>
      </RadioGroupContext.Provider>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

const RadioGroupItem = forwardRef<HTMLButtonElement, RadioGroupItemProps>(
  ({ className, disabled, onClick, onKeyDown, value, ...props }, ref) => {
    const context = useContext(RadioGroupContext);
    const isDisabled = disabled || context?.disabled;
    const isChecked = context?.value === value;

    return (
      <button
        ref={ref}
        type="button"
        // biome-ignore lint/a11y/useSemanticElements: This design-system radio item uses button semantics to support the existing roving-focus implementation.
        role="radio"
        aria-checked={isChecked}
        aria-disabled={isDisabled || undefined}
        data-state={isChecked ? 'checked' : 'unchecked'}
        disabled={isDisabled}
        name={context?.name}
        value={value}
        className={cn(
          'border-input ring-offset-background focus:ring-ring size-4 rounded-full border focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          isChecked && 'border-primary',
          className
        )}
        onClick={(event) => {
          onClick?.(event);

          if (event.defaultPrevented || isDisabled) {
            return;
          }

          context?.onValueChange?.(value);
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);

          if (event.defaultPrevented || isDisabled) {
            return;
          }

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            context?.onValueChange?.(value);
          }
        }}
        {...props}
      >
        {isChecked && (
          <span className="flex items-center justify-center">
            <Circle className="fill-primary text-primary size-2.5" />
          </span>
        )}
      </button>
    );
  }
);

RadioGroupItem.displayName = 'RadioGroupItem';

export { RadioGroup, RadioGroupItem };
